/**
 * Per-watershed "change over time" readout (trail #temporal-insight).
 *
 * Pure data + HTML-string builders, framework-agnostic like
 * obs-popup.ts / dc311-popup.ts, so the SAME markup renders inside both
 * the imperative Leaflet watershed popup and the Preact pill body
 * (via dangerouslySetInnerHTML) — one function, two call sites, no
 * chance of the two surfaces drifting apart.
 *
 * Bars respect the verified-filter (the caller pre-filters `members`
 * through rendersOnMap(..., 'all')) but deliberately IGNORE the
 * time-window control — they ARE the time context the window control
 * lets you narrow away from.
 */

import { escapeHtml as escape } from './dc311-popup';
import { realObservedAtMs } from './time-window';

export interface YearBarDatum {
  year: number;
  count: number;
}

interface ObsFeatureLike {
  properties?: unknown;
}

const YEAR_MS = 365 * 86_400_000;

/**
 * Per-calendar-year counts from `startYearFloor` (default 2019 — first
 * year real dated data exists) through the current year, using each
 * record's honest observed_at (time-window.ts's realObservedAtMs —
 * undated/fabricated-snapshot OLM rows are excluded, not zeroed into
 * "this year"). Capped to the most recent `maxBars` years (default 8)
 * so a distant future doesn't grow the strip unboundedly.
 */
export function computeYearBars(
  members: readonly ObsFeatureLike[],
  opts: { nowMs?: number; startYearFloor?: number; maxBars?: number } = {},
): YearBarDatum[] {
  const nowMs = opts.nowMs ?? Date.now();
  const floor = opts.startYearFloor ?? 2019;
  const maxBars = opts.maxBars ?? 8;
  const currentYear = new Date(nowMs).getUTCFullYear();
  const startYear = Math.max(floor, currentYear - maxBars + 1);

  const counts = new Map<number, number>();
  for (let y = startYear; y <= currentYear; y++) counts.set(y, 0);
  for (const f of members) {
    const ms = realObservedAtMs(f);
    if (ms === null) continue;
    const y = new Date(ms).getUTCFullYear();
    if (counts.has(y)) counts.set(y, (counts.get(y) ?? 0) + 1);
  }
  return [...counts.entries()].map(([year, count]) => ({ year, count }));
}

/** "Past 12 months: {n} · prior 12: {m}" counts, honest-date only. */
export function pastVsPriorYear(
  members: readonly ObsFeatureLike[],
  nowMs: number = Date.now(),
): { past12: number; prior12: number } {
  let past12 = 0;
  let prior12 = 0;
  for (const f of members) {
    const ms = realObservedAtMs(f);
    if (ms === null) continue;
    const age = nowMs - ms;
    if (age < 0) continue;
    if (age < YEAR_MS) past12++;
    else if (age < 2 * YEAR_MS) prior12++;
  }
  return { past12, prior12 };
}

/** aria-label per spec: "reports by year: 2021: 96, 2022: 40, …". */
export function yearBarsAriaLabel(bars: readonly YearBarDatum[]): string {
  return 'reports by year: ' + bars.map((b) => `${b.year}: ${b.count}`).join(', ');
}

/**
 * Plain-<div> bar strip (no chart lib) + the "past 12 / prior 12"
 * sentence. One gold hue; tabular-num year labels at the first/last
 * bar only (per spec — not every bar, to keep it a glance-read). Empty
 * string when there's nothing to show (zero members).
 */
export function buildYearBarHtml(
  bars: readonly YearBarDatum[],
  pastVsPrior: { past12: number; prior12: number },
): string {
  if (bars.length === 0) return '';
  // A strip where every year is zero means no member carried a REAL date
  // (computeYearBars skips undated records but always emits the full year
  // range). A flat all-zero chart plus "Past 12 months: 0 · prior 12: 0"
  // is misleading noise, not insight — suppress entirely; the pill's
  // undated note already explains why no trend is available.
  if (bars.every((b) => b.count === 0) && pastVsPrior.past12 === 0 && pastVsPrior.prior12 === 0) {
    return '';
  }
  const max = Math.max(1, ...bars.map((b) => b.count));
  const barsHtml = bars
    .map((b) => {
      // Zero-count years still get a thin visible baseline tick so the
      // strip reads as "8 years, some empty" rather than missing bars.
      const pct = b.count > 0 ? Math.max(8, Math.round((b.count / max) * 100)) : 3;
      return `<div class="trail-yearbar__bar" style="height:${pct}%" title="${escape(String(b.year))}: ${escape(String(b.count))}"></div>`;
    })
    .join('');
  const first = bars[0];
  const last = bars[bars.length - 1];
  const { past12, prior12 } = pastVsPrior;
  return (
    `<div class="trail-yearbar" role="img" aria-label="${escape(yearBarsAriaLabel(bars))}">` +
    `<div class="trail-yearbar__bars">${barsHtml}</div>` +
    `<div class="trail-yearbar__labels">` +
    `<span>${escape(String(first.year))}</span><span>${escape(String(last.year))}</span>` +
    `</div>` +
    `</div>` +
    `<div class="trail-yearbar__sentence">Past 12 months: ${past12.toLocaleString()} report${past12 === 1 ? '' : 's'} · prior 12: ${prior12.toLocaleString()}</div>`
  );
}
